/**
 * CORE-1200 — блок «Код ошибки … / Скопировать код» под баннером ошибки.
 *
 * Живёт отдельным компонентом, чтобы у входа и регистрации он был ОДИН, а не
 * две копии: первая копия существовала только на входе, и человек, пришедший
 * по приглашению и заводивший аккаунт, оставался без кода вовсе.
 *
 * Стили приходят пропсами, а не импортируются: шкала типографики и палитра
 * экспортируются из `RegisterScreen`, и импорт оттуда замкнул бы цикл
 * (`RegisterScreen` → этот компонент → `RegisterScreen`).
 */
import React from 'react';
import { View, Text, TouchableOpacity, type StyleProp, type TextStyle, type ViewStyle } from 'react-native';

import { useI18n } from '../../i18n';

interface AuthErrorCodeProps {
  /** Код попытки. `null` — блока нет вовсе. */
  code: string | null;
  /** Копирование уже удалось — подпись кнопки меняется на «Скопировано». */
  copied: boolean;
  onCopy: (code: string) => void;
  /** `login` | `register` — из него собираются testID. */
  testIDPrefix: string;
  containerStyle?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  hintStyle?: StyleProp<TextStyle>;
  linkStyle?: StyleProp<TextStyle>;
  linkContainerStyle?: StyleProp<ViewStyle>;
}

export function AuthErrorCode({
  code,
  copied,
  onCopy,
  testIDPrefix,
  containerStyle,
  textStyle,
  hintStyle,
  linkStyle,
  linkContainerStyle,
}: AuthErrorCodeProps) {
  const { t } = useI18n();
  if (!code) return null;

  return (
    <View style={containerStyle} testID={`${testIDPrefix}-error-code-block`}>
      {/* Текст `selectable` намеренно: если буфер обмена недоступен (веб в
          небезопасном контексте), код всё равно можно выделить руками. */}
      <Text testID={`${testIDPrefix}-error-code`} selectable style={textStyle}>
        {`${t('login.errorCodeLabel')}: ${code}`}
      </Text>
      <Text style={hintStyle}>{t('login.errorCodeHint')}</Text>
      <TouchableOpacity
        testID={`${testIDPrefix}-error-code-copy`}
        onPress={() => onCopy(code)}
        accessibilityRole="button"
        style={linkContainerStyle}
      >
        <Text style={linkStyle}>
          {copied ? t('login.errorCodeCopied') : t('login.errorCodeCopy')}
        </Text>
      </TouchableOpacity>
    </View>
  );
}
